import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { LoginRequest } from '../types/auth';
import { apiErrorMessage } from '../utils/apiErrors';
import { getSessionNotice, sessionNoticeText } from '../utils/session';

interface LoginProps {
  onSwitchToRegister: () => void;
  onBack?: () => void;
}

const Login: React.FC<LoginProps> = ({ onSwitchToRegister, onBack }) => {
  const { login, loading, sessionEndCount } = useAuth();
  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
  });
  const [error, setError] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  // [Web UX 2. csomag – Belépés és munkamenet, 2026-10-03] A lejárt/véget ért munkamenet üzenete
  // (`utils/session.ts`, `sessionStorage`-jelző): túléli az újratöltést, a következő SIKERES
  // belépés törli (`AuthContext.login`), kézi kijelentkezés után nincs.
  const [sessionNotice, setSessionNoticeText] = useState<string | null>(() =>
    sessionNoticeText(getSessionNotice())
  );
  // [validátor M1, 2026-10-03] Lejárt tárolt tokennel KÖZVETLENÜL a `/belepes`-t újratöltve az első
  // render még a jelző BEÁLLÍTÁSA ELŐTT fut: az `AuthContext` induló vizsgálata effektben írja a
  // jelzőt (`AuthContext.tsx`, `initializeAuth`), és a gyermek (Login) első renderje ezt megelőzi —
  // az üzenet nem jelent meg. A munkamenet minden végén a `sessionEndCount` nő, erre újraolvassuk.
  useEffect(() => {
    setSessionNoticeText(sessionNoticeText(getSessionNotice()));
  }, [sessionEndCount]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      // [Web UX 2. csomag, 2026-10-03] Az e-mail cím végén/elején álló szóköz (a billentyűzet-javaslat
      // gyakran tesz utána egyet) „Hibás e-mail cím vagy jelszó"-t adott (a mobil jelentés 1.4
      // pontjában mérve). A JELSZÓT szándékosan NEM vágjuk: a szóköz a jelszó része lehet.
      await login({ ...formData, email: formData.email.trim() });
    } catch (err: any) {
      console.error('Login error:', err);

      // Egységes hibaszöveg: megkülönbözteti a szerver-választ (van `message`),
      // a szerver-választ üzenet nélkül (fallback + HTTP-státusz) és a hálózati
      // hibát (a kérés el sem érte a szervert) — lásd utils/apiErrors.ts.
      const errorMessage = apiErrorMessage(err, 'Bejelentkezési hiba történt');
      let displayError = '';

      // Check for specific error types
      if (errorMessage.includes('pending approval') ||
        errorMessage.includes('jóváhagyásra vár') ||
        errorMessage.includes('Your account is pending approval')) {
        displayError = 'A fiókod még adminisztrátori jóváhagyásra vár. Kérjük, várd meg a jóváhagyást, majd próbálkozz újra a bejelentkezéssel.';
      } else if (errorMessage.includes('Helytelen e-mail cím vagy jelszó') ||
        errorMessage.includes('invalid credentials') ||
        errorMessage.includes('hibás') ||
        errorMessage.includes('Invalid')) {
        displayError = 'Hibás e-mail cím vagy jelszó.';
      } else if (errorMessage.includes('inaktív') ||
        errorMessage.includes('inactive')) {
        displayError = 'A fiók inaktív. Kérjük, lépj kapcsolatba az adminisztrátorral.';
      } else if ((err?.response?.status ?? 0) >= 500) {
        // 500-as válasznál SZÁNDÉKOSAN nem a szerver mondatát írjuk ki. A szerver üzenete ma már
        // magyar („Hiba történt a bejelentkezés során"), de a belső hiba okáról a belépő
        // képernyőn semmi értelmes nem mondható, és a keretrendszer 500-as válaszai (vagy egy
        // proxy hibaoldala) továbbra is angolok lehetnek. A döntés STÁTUSZKÓD-alapú, nem
        // szöveg-alapú — így egy jövőbeli üzenet-átírás nem tud kiengedni idegen szöveget.
        // A hálózati hibát és az ismert (401-es) magyar üzeneteket a fenti ágak fogják.
        displayError = 'Szerverhiba történt a belépésnél. Próbáld újra kicsit később.';
      } else {
        // Ismeretlen szerver-üzenet vagy hálózati hiba: az apiErrorMessage által adott szöveg.
        displayError = errorMessage;
      }

      setError(displayError);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%', position: 'relative' }}>
          {onBack && (
            <IconButton
              onClick={onBack}
              sx={{ position: 'absolute', top: 8, left: 8 }}
              aria-label="Vissza"
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Bejelentkezés
          </Typography>

          {sessionNotice && !error && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="session-expired-alert">
              {sessionNotice}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="E-mail cím"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              data-testid="email-input"
              aria-label="E-mail cím beviteli mező"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Jelszó"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              /* [Web UX 2. csomag, 2026-10-03] A „Minimum 6 karakter szükséges" súgó TÖRÖLVE (a régi
                 `Login.tsx:141`): a belépésnél nem jelszót választ a felhasználó, a súgó félrevezető
                 volt (jelentés 2.4). A jelszó-mutatás a `Register.tsx` mintáját követi; a gomb neve
                 SZÁNDÉKOSAN nem tartalmazza a „Bejelentkezés" szót (az E2E `getByRole('button',
                 { name: /Bejelentkezés/i })` szelektorai miatt, pl. `flow-profile-password.spec.ts`). */
              data-testid="password-input"
              aria-label="Jelszó beviteli mező"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Jelszó elrejtése' : 'Jelszó megjelenítése'}
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      disabled={loading}
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
              data-testid="login-submit-button"
              aria-label="Bejelentkezés gomb"
            >
              {loading ? <CircularProgress size={24} /> : 'Bejelentkezés'}
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={onSwitchToRegister}
              disabled={loading}
              data-testid="switch-to-register-button"
              aria-label="Regisztráció gomb"
            >
              Nincs még fiókod? Regisztrálj!
            </Button>
            <Button
              fullWidth
              variant="text"
              href="/forgot-password"
              sx={{ mt: 1 }}
              data-testid="forgot-password-button"
            >
              Elfelejtetted a jelszavad?
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
